import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch, apiGet, apiPost, apiPatch, apiDelete, ApiClientError } from '@client/lib/api';
import { formatCents, parseCents, sumCents } from '@shared/money';
import { mapsUrl } from '@shared/maps';
import { type JobStatus, MAX_UPLOAD_BYTES, ALLOWED_UPLOAD_MIME_TYPES } from '@shared/constants';
import type { AttachmentCategory } from '@shared/constants';
import { useSession } from '@client/lib/useSession';
import styles from './JobDetailPage.module.css';

/** The full job detail returned by GET /api/v1/jobs/:id. */
interface JobDetail {
  id: string;
  title: string;
  status: JobStatus;
  scheduledDate: string;
  scheduledStartTime: string;
  priceCents?: number;
  propertyId: string;
  customerId: string;
  organizationId: string;
  assignedUserId?: string | null;
  /** Extra assignees, owner payload only (server joins them for the editor). */
  additionalAssignees?: Array<{ id: string; displayName: string }>;
  notes?: string | null;
  invoicedAt?: string | null;
  customer?: Customer | null;
  property?: Property | null;
  services?: Service[];
  /** Owner payload only: every payment on every invoice carrying this job. */
  payments?: JobPayment[];
}

/** One row of the owner-only `payments` array on GET /api/v1/jobs/:id. */
interface JobPayment {
  id: string;
  invoiceId: string;
  amountCents: number;
  paymentMethod: string;
  paidAt: string;
  reference: string | null;
  voidedAt: string | null;
}

/** A team member as returned by GET /api/v1/team (OWNER only — not in CREW_ALLOWED). */
interface TeamMember {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string;
  phone: string;
  email: string;
}

interface Property {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  gateCode: string | null;
  accessNotes: string | null;
  petNotes: string | null;
  hazardNotes: string | null;
}

interface Service {
  id: string;
  description: string;
  priceCents: number;
  estimatedDurationMinutes: number;
  isCompleted: boolean;
  sortOrder: number;
}

/** A row from GET /api/v1/attachments?entityType=job&entityId=<id>. */
interface Attachment {
  id: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  category: AttachmentCategory;
  caption: string | null;
  createdAt: string;
}

/**
 * The transitions legal from each status. This is a DISPLAY-ONLY derivation:
 * the server is the authority and enforces these rules (returning 409 for an
 * illegal move). We never treat the client as the source of truth.
 */
const TRANSITIONS: Record<JobStatus, { label: string; to: JobStatus }[]> = {
  SCHEDULED: [
    { label: 'Start', to: 'IN_PROGRESS' },
    { label: 'Complete', to: 'COMPLETED' },
    { label: 'Skip', to: 'SKIPPED' },
    { label: 'Cancel', to: 'CANCELLED' },
  ],
  IN_PROGRESS: [
    { label: 'Complete', to: 'COMPLETED' },
    { label: 'Skip', to: 'SKIPPED' },
    { label: 'Cancel', to: 'CANCELLED' },
    { label: 'Back to Scheduled', to: 'SCHEDULED' },
  ],
  COMPLETED: [
    { label: 'Reschedule', to: 'SCHEDULED' },
    { label: 'Cancel', to: 'CANCELLED' },
  ],
  SKIPPED: [{ label: 'Reschedule', to: 'SCHEDULED' }],
  CANCELLED: [{ label: 'Reschedule', to: 'SCHEDULED' }],
};

type ViewState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; job: JobDetail };

function formatAddress(property: Property): string {
  const parts = [
    property.addressLine1,
    property.addressLine2,
    property.city,
    property.state,
    property.postalCode,
  ].filter((p): p is string => p != null && p !== '');
  return parts.join(', ');
}

/**
 * Render the property address as a link to the maps app when there is an
 * address worth linking, or as plain text otherwise. The link opens in a new
 * tab with noopener so the destination cannot reach back into this page via
 * window.opener.
 */
function renderPropertyAddress(property: Property): ReactNode {
  const address = formatAddress(property);
  const url = mapsUrl(address);
  if (url === null) {
    return address;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${address} in maps`}
      className={styles.propertyAddress}
    >
      {address}
    </a>
  );
}

function formatCustomerName(customer: Customer): string {
  return `${customer.firstName} ${customer.lastName}`;
}

/** True when the attachment is an image we can render as a thumbnail. */
function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/** True when the attachment is a PDF, which has no thumbnail. */
function isPdf(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

/** Build the authenticated content URL for an attachment's bytes. */
function attachmentContentUrl(id: string): string {
  return `/api/v1/attachments/${id}/content`;
}

/** Format a byte count as a human-readable string. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useSession();
  const isOwner = user?.role === 'OWNER';
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // A ref guards the double-fire: two clicks in one tick both run before
  // React re-renders, so a boolean state flag would not catch the second.
  const uploadingRef = useRef(false);

  // --- Edit form ---
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editPriceInput, setEditPriceInput] = useState('');
  const [editAssignedUserId, setEditAssignedUserId] = useState('');
  const [editAdditionalIds, setEditAdditionalIds] = useState<string[]>([]);
  const [editError, setEditError] = useState<string | null>(null);
  // Team members for the assignee display and edit select. GET /team is
  // OWNER-only (roles.ts CREW_ALLOWED), so the fetch — and everything that
  // reads the result — is gated on the role, not on data presence.
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [teamFailed, setTeamFailed] = useState(false);
  /** One row of GET /jobs/:id/completions. Named so the api calls stay flat-generic. */
  interface CompletionRow {
    userId: string;
    displayName: string;
  }

  /** Who has confirmed completion, from GET /jobs/:id/completions. */
  const [completions, setCompletions] = useState<Array<{ userId: string; displayName: string }>>(
    []
  );
  const [confirming, setConfirming] = useState(false);
  // A ref guards the double-save: two taps in one tick both run before React
  // re-renders, so a boolean state flag would not catch the second.
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Per-attachment state for the remove flow.
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [expandedAttachment, setExpandedAttachment] = useState<Attachment | null>(null);
  // A ref guards the double-fire per attachment: two clicks in one tick both
  // run before React re-renders, so a boolean state flag would not catch the
  // second. Keyed per attachment id so one photo deleting does not disable
  // the others.
  const removingRefs = useRef<Record<string, boolean>>({});

  const fetchJob = () => {
    if (!id) return;
    setState({ kind: 'loading' });
    setTransitionError(null);
    apiGet<JobDetail>(`/api/v1/jobs/${id}`)
      .then((job) => {
        // A CREW member receives propertyId but no property object.
        // Fetch the property so the address and access details render.
        if (!job.property && job.propertyId) {
          apiGet<Property>(`/api/v1/properties/${job.propertyId}`)
            .then((property) => {
              setState({ kind: 'loaded', job: { ...job, property } });
            })
            .catch(() => {
              // A failed property fetch is not fatal: render the job
              // without the property details.
              setState({ kind: 'loaded', job });
            });
        } else {
          setState({ kind: 'loaded', job });
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiClientError && err.status === 404) {
          setState({ kind: 'not-found' });
        } else if (err instanceof ApiClientError) {
          setState({ kind: 'error', message: err.message });
        } else {
          setState({ kind: 'error', message: 'Something went wrong.' });
        }
      });
  };

  const fetchAttachments = () => {
    if (!id) return;
    setAttachmentsLoading(true);
    apiGet<Attachment[]>(`/api/v1/attachments?entityType=job&entityId=${id}`)
      // Coerce to an array. The catch below covers a REJECTED fetch, but a
      // resolved-yet-unexpected body (a shape change, a proxy returning an
      // object) would put a non-array in state and make attachments.map throw
      // during render -- which unmounts the WHOLE screen, job details and all.
      // Photos are secondary; they must never be able to take the page down.
      .then((rows) => setAttachments(Array.isArray(rows) ? rows : []))
      .catch(() => {
        // Attachments are a secondary concern; a failure here must not
        // break the rest of the screen.
        setAttachments([]);
      })
      .finally(() => setAttachmentsLoading(false));
  };

  useEffect(() => {
    fetchJob();
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The assignee NAME needs the team list, and /team is owner-only (roles.ts
  // CREW_ALLOWED). A crew member's payload already carries assignedUserId, but
  // there is no crew-readable source of names, so crew sees no assignee row
  // at all — the gate is the role, never the field's presence.
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    apiGet<TeamMember[]>('/api/v1/team')
      .then((members) => {
        if (!cancelled) {
          setTeam(members);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTeamFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  // Completion confirmations: who has confirmed, for the confirm UI and the
  // owner's missing-names view. The endpoint is crew-readable for assigned
  // jobs (roles.ts), so every role fetches it; a 404/empty just renders none.
  const fetchCompletions = useCallback(() => {
    if (!id) return;
    apiGet<CompletionRow[]>(`/api/v1/jobs/${id}/completions`)
      .then((rows) => setCompletions(rows))
      .catch(() => setCompletions([]));
  }, [id]);

  useEffect(() => {
    fetchCompletions();
  }, [fetchCompletions]);

  const confirmCompletion = async () => {
    if (!id || confirming) return;
    setConfirming(true);
    try {
      const rows = await apiPost<CompletionRow[]>(`/api/v1/jobs/${id}/completions`, {});
      setCompletions(rows);
    } catch {
      // The button retries; a failed confirm must not blank the screen.
    } finally {
      setConfirming(false);
    }
  };

  const handleTransition = (to: JobStatus) => {
    if (!id || transitioning) return;
    setTransitioning(true);
    setTransitionError(null);
    apiPost<{ status: JobStatus }>(`/api/v1/jobs/${id}/status`, { status: to })
      .then((result) => {
        // Re-render from the SERVER's response: update the job's status.
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            job: { ...prev.job, status: result.status },
          };
        });
      })
      .catch((err: unknown) => {
        // On failure the job keeps its ORIGINAL status. The server's
        // message (including 409 conflicts) is surfaced to the owner.
        if (err instanceof ApiClientError) {
          setTransitionError(err.message);
        } else {
          setTransitionError('Something went wrong.');
        }
      })
      .finally(() => {
        setTransitioning(false);
      });
  };

  const handleUpload = (file: File) => {
    // Guard the double-fire with a ref: two clicks in one tick both run
    // before React re-renders, so a boolean state flag would not catch the
    // second. A duplicate upload costs real storage.
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    setUploading(true);
    setUploadError(null);

    // Reject an oversized or wrong-type file BEFORE uploading, naming the
    // actual limit so the owner knows what the server will accept.
    if (
      !ALLOWED_UPLOAD_MIME_TYPES.includes(file.type as (typeof ALLOWED_UPLOAD_MIME_TYPES)[number])
    ) {
      setUploadError(
        `Unsupported file type. Allowed types: ${ALLOWED_UPLOAD_MIME_TYPES.join(', ')}.`
      );
      uploadingRef.current = false;
      setUploading(false);
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(`File is too large. The maximum size is ${MAX_UPLOAD_BYTES} bytes.`);
      uploadingRef.current = false;
      setUploading(false);
      return;
    }

    if (!id) {
      uploadingRef.current = false;
      setUploading(false);
      return;
    }

    // The file is the RAW REQUEST BODY, not multipart/form-data. The
    // Content-Type header names the MIME type and the body length is the
    // size — both are observed server-side. apiFetch attaches the CSRF
    // header on POST and forwards the session cookie.
    apiFetch<Attachment>(`/api/v1/attachments?entityType=job&entityId=${id}`, {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': file.type },
    })
      .then(() => {
        // Refresh the list so the new photo appears without a reload.
        fetchAttachments();
      })
      .catch((err: unknown) => {
        if (err instanceof ApiClientError) {
          setUploadError(err.message);
        } else {
          setUploadError('Something went wrong.');
        }
      })
      .finally(() => {
        uploadingRef.current = false;
        setUploading(false);
      });
  };

  const beginEdit = (job: JobDetail) => {
    setEditTitle(job.title);
    setEditDate(job.scheduledDate);
    setEditStartTime(job.scheduledStartTime);
    setEditPriceInput(job.priceCents != null ? formatCents(job.priceCents) : '');
    setEditAssignedUserId(job.assignedUserId ?? '');
    setEditAdditionalIds((job.additionalAssignees ?? []).map((a) => a.id));
    setEditError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditError(null);
  };

  const handleSave = async () => {
    if (!id || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setEditError(null);

    // Diff against the loaded job: send ONLY the fields that changed.
    // Sending an unchanged field would clobber a concurrent edit.
    const body: Record<string, unknown> = {};
    if (editTitle !== job.title) body.title = editTitle;
    if (editDate !== job.scheduledDate) body.scheduledDate = editDate;
    // An empty input and a null start time are the SAME state. Comparing them
    // directly ('' !== null) makes an untouched start time look changed on
    // every save, which sends a field the user never edited -- the exact
    // clobbering this diff exists to prevent.
    const currentStartTime = job.scheduledStartTime ?? '';
    if (editStartTime !== currentStartTime) {
      body.scheduledStartTime = editStartTime === '' ? null : editStartTime;
    }
    const parsedPrice = parseCents(editPriceInput);
    if (parsedPrice !== null && parsedPrice !== job.priceCents) {
      body.priceCents = parsedPrice;
    }
    // Assignment diff: the loaded value's absence and an empty select are the
    // same state (see the start-time note above), and clearing the select
    // sends an explicit null — PATCH { assignedUserId: null } is how the
    // server unassignes; omitting the key would leave the assignment untouched.
    const currentAssignedUserId = job.assignedUserId ?? '';
    if (editAssignedUserId !== currentAssignedUserId) {
      body.assignedUserId = editAssignedUserId === '' ? null : editAssignedUserId;
    }
    // Extras diff: compare as sets so a reorder is not an edit; replace-all
    // when different, absent key when not.
    const currentExtras = (job.additionalAssignees ?? []).map((a) => a.id);
    const sameExtras =
      editAdditionalIds.length === currentExtras.length &&
      [...editAdditionalIds].sort().join() === [...currentExtras].sort().join();
    if (!sameExtras) {
      body.additionalAssigneeIds = [...editAdditionalIds].sort();
    }

    // Nothing changed: close the form without a request. A PATCH with an empty
    // body is a round trip that can only fail or do nothing.
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }

    try {
      const updated = await apiPatch<JobDetail>(`/api/v1/jobs/${id}`, body);
      setState({ kind: 'loaded', job: updated });
      setEditing(false);
    } catch (err: unknown) {
      if (err instanceof ApiClientError) {
        setEditError(err.message);
      } else {
        setEditError('Something went wrong.');
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Delete this job? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await apiDelete(`/api/v1/jobs/${id}`);
      // Navigate back to the jobs list after deletion.
      window.location.href = '/jobs';
    } catch (err: unknown) {
      if (err instanceof ApiClientError) {
        setEditError(err.message);
      } else {
        setEditError('Something went wrong.');
      }
      setDeleting(false);
    }
  };

  const handleRemovePhoto = async (attachment: Attachment) => {
    // Guard the double-fire with a ref keyed per attachment id: two clicks in
    // one tick both run before React re-renders, so a boolean state flag would
    // not catch the second. One photo deleting must not disable the others.
    if (removingRefs.current[attachment.id]) return;
    removingRefs.current[attachment.id] = true;
    setRemovingIds((prev) => new Set(prev).add(attachment.id));
    setRemoveError(null);
    try {
      await apiDelete(`/api/v1/attachments/${attachment.id}`);
      // Remove it from the list without a full reload.
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
      // If the expanded detail was this attachment, close it.
      if (expandedAttachment?.id === attachment.id) {
        setExpandedAttachment(null);
      }
    } catch (err: unknown) {
      // On failure, show the server's message and leave the photo in place.
      if (err instanceof ApiClientError) {
        setRemoveError(err.message);
      } else {
        setRemoveError('Something went wrong.');
      }
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(attachment.id);
        return next;
      });
      removingRefs.current[attachment.id] = false;
    }
  };

  const confirmRemovePhoto = (attachment: Attachment) => {
    if (
      window.confirm(
        `Remove this photo?\n\n${attachment.originalFilename}\n\nThis cannot be undone.`
      )
    ) {
      void handleRemovePhoto(attachment);
    }
  };

  const toggleExpandAttachment = (attachment: Attachment) => {
    setExpandedAttachment((prev) => (prev?.id === attachment.id ? null : attachment));
  };

  if (state.kind === 'loading') {
    return (
      <main className={styles.container} id="main">
        <p className={styles.loading}>Loading job…</p>
      </main>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <main className={styles.container} id="main">
        <p className={styles.notFound}>This job could not be found.</p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className={styles.container} id="main">
        <p className={styles.error}>{state.message}</p>
        <button type="button" className={styles.retryButton} onClick={fetchJob} aria-label="Retry">
          Retry
        </button>
      </main>
    );
  }

  const { job } = state;
  const transitions = TRANSITIONS[job.status];
  // The assignee's NAME, resolved from the team list. Undefined while the list
  // is missing or the id is unknown — the row then shows a neutral fallback
  // rather than a blank.
  const assignedUserName = job.assignedUserId
    ? team.find((member) => member.id === job.assignedUserId)?.displayName
    : undefined;

  return (
    <main className={styles.container} id="main">
      <header className={styles.header}>
        <h1 className={styles.title}>{job.title}</h1>
        <span className={styles.statusBadge}>{job.status}</span>
        {!editing && (
          <button
            type="button"
            className={styles.editButton}
            onClick={() => beginEdit(job)}
            aria-label="Edit job"
          >
            Edit
          </button>
        )}
      </header>

      {editing && (
        <section className={styles.editSection} aria-label="Edit job">
          {editError && (
            <span className={styles.editError} role="alert">
              {editError}
            </span>
          )}
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="edit-title">
              Title
            </label>
            <input
              id="edit-title"
              type="text"
              className={styles.editInput}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              aria-label="Title"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="edit-date">
              Scheduled date
            </label>
            <input
              id="edit-date"
              type="date"
              className={styles.editInput}
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              aria-label="Scheduled date"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="edit-start-time">
              Start time
            </label>
            <input
              id="edit-start-time"
              type="time"
              className={styles.editInput}
              value={editStartTime}
              onChange={(e) => setEditStartTime(e.target.value)}
              aria-label="Start time"
            />
          </div>
          {isOwner && (
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="edit-price">
                Price
              </label>
              <input
                id="edit-price"
                type="text"
                className={styles.editInput}
                value={editPriceInput}
                onChange={(e) => setEditPriceInput(e.target.value)}
                aria-label="Price"
              />
            </div>
          )}
          {isOwner && (
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="edit-assigned-to">
                Assigned to
              </label>
              <select
                id="edit-assigned-to"
                className={styles.editInput}
                value={editAssignedUserId}
                onChange={(e) => setEditAssignedUserId(e.target.value)}
                aria-label="Assigned to"
                disabled={teamFailed}
              >
                <option value="">Unassigned</option>
                {/* Inactive members cannot be (re)assigned work — the server's
                    validateAssignedUser refuses them. */}
                {team
                  .filter((member) => member.isActive)
                  .map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.displayName}
                    </option>
                  ))}
              </select>
            </div>
          )}
          {isOwner && (
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="edit-also-assign">
                Also assign
              </label>
              {/* Replace-all semantics server-side; excluded: the primary and
                  inactive members, for the same reasons as above. */}
              <select
                id="edit-also-assign"
                className={styles.editInput}
                multiple
                size={3}
                value={editAdditionalIds}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                  setEditAdditionalIds(selected);
                }}
                aria-label="Also assign"
                disabled={teamFailed}
              >
                {team
                  .filter(
                    (member) =>
                      member.isActive && member.id !== editAssignedUserId && member.id !== user?.id
                  )
                  .map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.displayName}
                    </option>
                  ))}
              </select>
            </div>
          )}
          <div className={styles.editActions}>
            {/*
              NOT "Cancel". The status controls on this same screen include a
              Cancel that cancels the JOB — a real, customer-visible action.
              Two buttons reading "Cancel" a few inches apart, one of which
              throws away an edit and one of which cancels the work, is a
              mis-tap waiting to happen.
            */}
            <button
              type="button"
              className={styles.editButton}
              onClick={cancelEdit}
              aria-label="Discard changes"
            >
              Discard changes
            </button>
            <button
              type="button"
              className={styles.actionButton}
              disabled={saving}
              onClick={handleSave}
              aria-label="Save"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Scheduled date</span>
          <span className={styles.fieldValue}>{job.scheduledDate}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Start time</span>
          <span className={styles.fieldValue}>{job.scheduledStartTime}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Property</span>
          <span className={styles.fieldValue}>
            {job.property ? renderPropertyAddress(job.property) : 'Address not available'}
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Customer</span>
          <span className={styles.fieldValue}>
            {job.customer ? formatCustomerName(job.customer) : 'Customer not available'}
          </span>
        </div>
        {isOwner && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Assigned to</span>
            <span className={styles.fieldValue}>
              {assignedUserName ?? (job.assignedUserId ? 'Assigned user' : 'Unassigned')}
            </span>
          </div>
        )}
        {isOwner && (job.additionalAssignees ?? []).length > 0 && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Also with</span>
            <span className={styles.fieldValue}>
              {(job.additionalAssignees ?? [])
                .map((a) => team.find((m) => m.id === a.id)?.displayName ?? 'crew')
                .join(', ')}
            </span>
          </div>
        )}
        {
          /* COMPLETION: the gate is server-side (409 naming the missing); this
             block makes the state visible and gives the assignee the button. */
          (isOwner ||
            job.assignedUserId === user?.id ||
            (job.additionalAssignees ?? []).some((a) => a.id === user?.id)) &&
            (job.assignedUserId || (job.additionalAssignees ?? []).length > 0) && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Completion confirmations</span>
                <span className={styles.fieldValue}>
                  {(() => {
                    const effective = [
                      ...(job.assignedUserId ? [job.assignedUserId] : []),
                      ...(job.additionalAssignees ?? []).map((a) => a.id),
                    ];
                    if (effective.length === 0) return null;
                    const confirmedIds = new Set(completions.map((c) => c.userId));
                    const nameFor = (uid: string) =>
                      completions.find((c) => c.userId === uid)?.displayName ??
                      (isOwner ? (team.find((m) => m.id === uid)?.displayName ?? 'crew') : null);
                    return effective
                      .map((uid) => {
                        const name = nameFor(uid);
                        return confirmedIds.has(uid)
                          ? `${name ?? uid}: confirmed`
                          : `${isOwner ? (name ?? uid) : 'You'}: waiting`;
                      })
                      .join(' · ');
                  })()}
                </span>
                {!isOwner &&
                  job.assignedUserId === user?.id &&
                  !completions.some((c) => c.userId === user?.id) && (
                    <button
                      type="button"
                      className={styles.editButton}
                      disabled={confirming}
                      onClick={() => void confirmCompletion()}
                    >
                      {confirming ? 'Confirming…' : 'Confirm done'}
                    </button>
                  )}
                {!isOwner &&
                  job.assignedUserId !== user?.id &&
                  (job.additionalAssignees ?? []).some((a) => a.id === user?.id) &&
                  !completions.some((c) => c.userId === user?.id) && (
                    <button
                      type="button"
                      className={styles.editButton}
                      disabled={confirming}
                      onClick={() => void confirmCompletion()}
                    >
                      {confirming ? 'Confirming…' : 'Confirm done'}
                    </button>
                  )}
              </div>
            )
        }
        {job.property &&
          (job.property.gateCode ||
            job.property.accessNotes ||
            job.property.petNotes ||
            job.property.hazardNotes) && (
            <section className={styles.section}>
              <span className={styles.fieldLabel}>Access details</span>
              <div className={styles.accessDetails}>
                {job.property.gateCode && (
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Gate code</span>
                    <span className={styles.fieldValue}>{job.property.gateCode}</span>
                  </div>
                )}
                {job.property.accessNotes && (
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Access notes</span>
                    <span className={styles.fieldValue}>{job.property.accessNotes}</span>
                  </div>
                )}
                {job.property.petNotes && (
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Pet notes</span>
                    <span className={styles.fieldValue}>{job.property.petNotes}</span>
                  </div>
                )}
                {job.property.hazardNotes && (
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Hazard notes</span>
                    <span className={styles.fieldValue}>{job.property.hazardNotes}</span>
                  </div>
                )}
              </div>
            </section>
          )}
      </section>

      {job.notes && (
        <section className={styles.section}>
          <span className={styles.fieldLabel}>Notes</span>
          <span className={styles.fieldValue}>{job.notes}</span>
        </section>
      )}

      {isOwner && job.services && (
        <section className={styles.section}>
          <span className={styles.fieldLabel}>Line items</span>
          <ul className={styles.lineItems}>
            {job.services
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((service) => (
                <li key={service.id} className={styles.lineItem}>
                  <span className={styles.lineItemDescription}>{service.description}</span>
                  <span className={styles.lineItemPrice}>{formatCents(service.priceCents)}</span>
                </li>
              ))}
          </ul>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Total</span>
            <span className={styles.fieldValue}>
              {job.priceCents != null ? formatCents(job.priceCents) : ''}
            </span>
          </div>
        </section>
      )}

      {/* PAYMENTS, owner-only (the server includes the array on the OWNER
          branch of GET /jobs/:id only). A job with no invoice carries no
          payments key at all, and the section must not render a stub for
          that. Voided payments stay visible, struck and muted: financial
          history is immutable, so hiding them would hide real money. */}
      {isOwner && (job.payments ?? []).length > 0 && (
        <section className={styles.section}>
          <span className={styles.fieldLabel}>Payments</span>
          <ul className={styles.paymentList}>
            {(job.payments ?? []).map((payment) => (
              <li key={payment.id} className={styles.paymentRow}>
                <Link to={`/invoices/${payment.invoiceId}`} className={styles.paymentLink}>
                  <span className={payment.voidedAt ? styles.paymentVoided : styles.paymentText}>
                    {formatCents(payment.amountCents)}
                  </span>
                  <span className={payment.voidedAt ? styles.paymentVoided : styles.paymentText}>
                    {payment.paymentMethod}
                  </span>
                </Link>
                <span className={payment.voidedAt ? styles.paymentVoided : styles.paymentText}>
                  {payment.paidAt.slice(0, 10)}
                  {payment.reference ? ` · ${payment.reference}` : ''}
                </span>
                {payment.voidedAt && <span className={styles.paymentVoidTag}>Void</span>}
              </li>
            ))}
          </ul>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Paid</span>
            <span className={styles.fieldValue}>
              {formatCents(
                sumCents(
                  (job.payments ?? []).filter((p) => p.voidedAt === null).map((p) => p.amountCents)
                )
              )}
            </span>
          </div>
        </section>
      )}

      {/* Invoices exist but nothing is recorded paid: the job's own
          invoicedAt says the invoice went out, so say the money is missing
          rather than silently blank. One muted line, gated on the role and on
          invoicedAt -- no invented status logic. A job never invoiced renders
          nothing here at all. */}
      {isOwner && job.invoicedAt && !(job.payments ?? []).some((p) => p.voidedAt === null) && (
        <section className={styles.section}>
          <span className={styles.fieldLabel}>Payments</span>
          <span className={styles.paymentEmpty}>Invoice sent — nothing recorded paid.</span>
        </section>
      )}

      {transitionError && (
        <section className={styles.section}>
          <span className={styles.transitionError}>{transitionError}</span>
        </section>
      )}

      <section className={styles.actions}>
        {transitions.map((t) => (
          <button
            key={t.to}
            type="button"
            className={styles.actionButton}
            disabled={transitioning}
            onClick={() => handleTransition(t.to)}
          >
            {transitioning ? '…' : t.label}
          </button>
        ))}
      </section>

      {isOwner && (
        <>
          {job.invoicedAt ? (
            <section className={styles.section}>
              <span className={styles.fieldLabel}>Delete</span>
              <span className={styles.fieldValue}>
                Not available: this job has been invoiced. Deleting billed work would leave an
                invoice line referring to nothing.
              </span>
            </section>
          ) : (
            <section className={styles.section}>
              <button
                type="button"
                className={styles.deleteButton}
                disabled={deleting}
                onClick={handleDelete}
                aria-label="Delete job"
              >
                {deleting ? 'Deleting…' : 'Delete job'}
              </button>
            </section>
          )}
        </>
      )}

      {/* NOT role-gated: src/server/middleware/roles.ts CREW_ALLOWED lists
          POST /attachments under "Proof of work" — a crew member on site is
          exactly who takes the before/after photos. */}
      {
        <section className={styles.photosSection} aria-label="Job photos">
          <header className={styles.photosHeader}>
            <h2 className={styles.photosTitle}>Photos</h2>
            <label className={styles.photoUploadForm}>
              <input
                type="file"
                accept={ALLOWED_UPLOAD_MIME_TYPES.join(', ')}
                disabled={uploading}
                aria-busy={uploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleUpload(file);
                  }
                  // Reset so the same file can be selected again.
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                className={styles.photoUploadButton}
                disabled={uploading}
                aria-busy={uploading}
                // A STABLE accessible name. The visible label switches to
                // 'Uploading…', and without this the control effectively renames
                // itself mid-action -- a screen reader user tracking it loses it,
                // and so does any query that looked it up by name. aria-busy is
                // what conveys the in-flight state.
                aria-label="Upload photo"
                onClick={() => {
                  const input = document.querySelector(
                    'input[type="file"]'
                  ) as HTMLInputElement | null;
                  input?.click();
                }}
              >
                {uploading ? 'Uploading…' : 'Upload photo'}
              </button>
            </label>
          </header>

          {uploadError && (
            <span className={styles.photoUploadError} role="alert">
              {uploadError}
            </span>
          )}

          {removeError && (
            <span className={styles.photoUploadError} role="alert">
              {removeError}
            </span>
          )}

          {attachments.length === 0 && !attachmentsLoading ? (
            <p className={styles.photoEmpty}>There are no photos yet.</p>
          ) : (
            <ul className={styles.photoGrid}>
              {attachments.map((attachment) => {
                const isRemoving = removingIds.has(attachment.id);
                const isExpanded = expandedAttachment?.id === attachment.id;
                return (
                  <li key={attachment.id} className={styles.photoGridItem}>
                    {isImage(attachment.mimeType) ? (
                      <img
                        className={styles.photoThumbnail}
                        src={attachmentContentUrl(attachment.id)}
                        alt={attachment.originalFilename}
                      />
                    ) : isPdf(attachment.mimeType) ? (
                      <a
                        className={styles.photoLink}
                        href={attachmentContentUrl(attachment.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className={styles.photoLinkIcon}>📄</span>
                        {attachment.originalFilename}
                      </a>
                    ) : (
                      <span className={styles.photoEmpty}>{attachment.originalFilename}</span>
                    )}
                    <button
                      type="button"
                      className={styles.photoRemoveButton}
                      disabled={isRemoving}
                      aria-busy={isRemoving}
                      aria-label={`Remove ${attachment.originalFilename}`}
                      onClick={() => confirmRemovePhoto(attachment)}
                    >
                      {isRemoving ? 'Removing…' : 'Remove'}
                    </button>
                    {isExpanded && (
                      <div className={styles.photoExpanded}>
                        <span className={styles.photoExpandedLabel}>
                          File size: {formatBytes(attachment.sizeBytes)}
                        </span>
                        <span className={styles.photoExpandedLabel}>
                          Uploaded: {new Date(attachment.createdAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.photoExpandButton}
                      aria-label={isExpanded ? 'Hide details' : 'Show details'}
                      onClick={() => toggleExpandAttachment(attachment)}
                    >
                      {isExpanded ? '−' : '+'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      }
    </main>
  );
}
